/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { RemovedWorkflow, WorkflowUsage } from '@/api/workflows';

/**
 * What deleting a template does to packets, in words (ADW-5498).
 *
 * A template that a packet template names as a step, or that a packet in
 * progress has still to start, is retired rather than deleted, and those
 * packets can no longer start that step. These say so before the delete, in
 * the toast after it, and on the step itself. Names and counts only.
 */

/** "1 packet" or "2 packets". */
function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** At most three names, then how many more. */
function names(list: readonly string[]): string {
  if (list.length <= 3) return list.join(', ');
  return `${list.slice(0, 3).join(', ')} and ${String(list.length - 3)} more`;
}

/**
 * The warning in the delete confirmation, or null when no packet uses it.
 */
export function deleteWarning(name: string, usage: WorkflowUsage): string | null {
  const templates = usage.packet_templates;
  const waiting = usage.packets_waiting;
  const inTemplates =
    templates.length > 0
      ? `${count(templates.length, 'packet template', 'packet templates')} (${names(templates)})`
      : null;
  const inProgress =
    waiting > 0 ? count(waiting, 'packet in progress', 'packets in progress') : null;

  if (inTemplates && inProgress) {
    return `${name} is a step in ${inTemplates} and in ${inProgress}. Deleting it retires it: those packets can't start this step and will need it skipped.`;
  }
  if (inProgress) {
    return `${name} is a step still to start in ${inProgress}. Deleting it retires it: ${waiting === 1 ? 'that packet' : 'those packets'} can't start this step and will need it skipped.`;
  }
  if (inTemplates) {
    return `${name} is a step in ${inTemplates}. Deleting it retires it: no new packet can be started from ${templates.length === 1 ? 'that packet template' : 'those packet templates'} until the step is taken out.`;
  }
  return null;
}

/** The toast once a template is removed: what actually happened. */
export function removedToast(result: RemovedWorkflow): string {
  if (result.outcome === 'deleted') return `${result.name} is gone.`;
  if (result.packets_waiting > 0) {
    const packets = count(result.packets_waiting, 'packet', 'packets');
    const has = result.packets_waiting === 1 ? 'has' : 'have';
    return `${result.name} was retired: ${packets} in progress ${has} it as a later step, which will need to be skipped.`;
  }
  if (result.agreements > 0) {
    return `${result.name} is retired. The ${count(result.agreements, 'agreement', 'agreements')} made from it ${result.agreements === 1 ? 'is' : 'are'} untouched.`;
  }
  return `${result.name} is retired: a packet or an agreement still uses it.`;
}

/** Said on a packet step whose template was deleted, in place of "Yours to prepare". */
export function retiredStepWarning(name: string, canSkip: boolean): string {
  const said = `This step's template, ${name}, was deleted, so it can't be started.`;
  return canSkip ? `${said} Skip this step to carry on with the packet.` : said;
}

/** Said on a packet template card with a step whose template was deleted. */
export const BUNDLE_STEP_DELETED =
  "A step's template was deleted, so no packet can be started from this until it is edited out.";
